import React, { useEffect, useRef, useState } from "react";
import { Search, Sparkles } from "lucide-react";
import { ScopeSelect, PLACEHOLDER } from "./ScopeSelect";
import { SearchDropdown } from "./SearchDropdown";
import { useMatches } from "./useSearch";
import AiSearchPanel from "./AiSearchPanel";

// Top-bar search: SKU, branch and supplier only, with an AI Search button that opens the slide-out panel.
export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [focus, setFocus] = useState(false);
  const [active, setActive] = useState(-1);
  const [panel, setPanel] = useState(null);
  const input = useRef(null);
  const { results, loading, error } = useMatches(q, kind);
  const term = q.trim();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); input.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ask = (req) => { setFocus(false); input.current?.blur(); setPanel({ ...req, nonce: Date.now() }); };
  const askText = () => ask(term.length >= 2 ? { q: term, kind } : { q: "", kind });
  const pick = (r) => ask({ q: r.code, kind: r.kind, code: r.code });
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, results.length)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (active >= 0 && active < results.length) pick(results[active]); else if (term.length >= 2) askText(); }
    else if (e.key === "Escape") { setFocus(false); input.current?.blur(); }
  };

  return (
    <>
      <div className="relative hidden md:flex items-center gap-1.5">
        <div className="flex items-center gap-2 pl-2 pr-2.5 h-9 bg-surface-2 border border-border rounded-sm w-60 lg:w-72 xl:w-80 text-sm focus-within:border-primary/60 transition-colors">
          <ScopeSelect value={kind} onChange={(k) => { setKind(k); setActive(-1); }} testid="global-search-scope" />
          <Search size={14} className="text-muted shrink-0" />
          <input ref={input} data-testid="global-search" value={q} maxLength={120} placeholder={PLACEHOLDER[kind]}
                 onChange={e => { setQ(e.target.value); setActive(-1); }} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} onKeyDown={onKeyDown}
                 className="bg-transparent outline-none w-full min-w-0 placeholder:text-subtle" />
          <kbd className="hidden lg:inline text-[10px] font-mono px-1.5 py-0.5 bg-bg border border-border rounded-sm text-muted">⌘K</kbd>
        </div>
        <button type="button" data-testid="global-search-ai-btn" onClick={askText} title="Search with AI"
                className="h-9 px-2.5 shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 border border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 active:scale-[0.97] transition-[background-color,transform] rounded-sm text-[12px] font-semibold">
          <Sparkles size={13} /><span className="hidden 2xl:inline">AI Search</span>
        </button>
        {focus && term.length >= 2 && (
          <SearchDropdown q={term} results={results} loading={loading} error={error} active={active} onPick={pick} onAsk={askText} />
        )}
      </div>
      <button type="button" data-testid="global-search-mobile-btn" onClick={() => ask({ q: "", kind })} aria-label="Search"
              className="md:hidden h-9 w-9 inline-flex items-center justify-center border border-border bg-surface-2 rounded-sm">
        <Search size={15} />
      </button>
      <AiSearchPanel req={panel} onClose={() => setPanel(null)} onSearch={ask} />
    </>
  );
}
